// ── Game Input Handlers ── Keyboard, mouse, settings, and weapon switching logic
// Extracted from Game class — each function takes a GameContext to access/mutate game state.

import { WEAPONS, type WeaponId, GRENADES, type GrenadeId } from './core/config';
import { settingsStore, type GameSettings } from './core/settings';
import { keybindManager } from './core/keybinds';
import { createWeaponState } from './gameplay/weapon';
import { getAvailableGrenades, getThrowSpeed } from './gameplay/grenade';
import { setLoadoutSlot } from './gameplay/loadout';
import { attachTPPWeapon } from './gameplay/tpp-weapon';
import { type GameContext, NORMAL_FOV } from './game-types';
import { getScopeType } from './ui/scope-overlay';
import { audioManager } from './audio/audio-manager';
import { playWeaponInspect } from './audio/sounds';
import { voiceChat } from './audio/voice';
import { setLightmapQuality } from './renderer/scene';
import { setPropLODQuality } from './map/props';
import { applyColorblindCSS } from './renderer/colorblind';

/**
 * True when the keyboard event target is an editable element (input, textarea,
 * select, or contenteditable). Game keybinds must skip these so the user can
 * type into the auth screen, chat boxes, clan-name dialog, etc., without
 * letters like Z/X/C/B/T being eaten by radio/buy/chat handlers.
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object') return false;
  const t = target as { tagName?: string; isContentEditable?: boolean };
  if (t.isContentEditable === true) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function handleKeyDown(ctx: GameContext, e: KeyboardEvent): void {
  // Skip all gameplay keybinds when the user is typing into an input. This
  // protects every text field in the app (auth screen, clan create, practice
  // command bar, etc.) from radio/buy/chat shortcuts swallowing characters.
  if (isEditableTarget(e.target)) return;

  const kb = keybindManager;
  const canUseChat = ctx.state === 'playing' || ctx.state === 'dead' || ctx.state === 'spectating';

  // Team select menu is open — let it handle its own keys, block everything else
  if (ctx.teamSelectMenu.isOpen()) return;

  // If chat or radio is already open, let it consume the key before gameplay
  if (ctx.chatUI.isOpen() && ctx.chatUI.handleKeyDown(e)) {
    e.preventDefault();
    return;
  }

  // Settings menu toggle (Escape — always hardcoded, not rebindable)
  if (e.code === 'Escape' && (ctx.state === 'playing' || ctx.state === 'dead' || ctx.state === 'spectating')) {
    // BUG-W-2: Close buy menu FIRST and return early. Previously the BuyMenu
    // also had its own document keydown listener and hid on Escape, after
    // which this handler would see `!isOpen()` and pop the settings menu in
    // the same keystroke.
    if (ctx.buyMenu.isOpen()) {
      ctx.buyMenu.hide();
      return;
    }
    if (ctx.settingsMenu.isOpen()) {
      ctx.settingsMenu.hide();
      return;
    }
    ctx.settingsMenu.show();
    return;
  }

  // Don't process game keys if settings menu is open
  if (ctx.settingsMenu.isOpen()) return;

  // Team select menu toggle
  if (kb.isAction('teamSelect', e.code) && (ctx.state === 'playing' || ctx.state === 'dead')) {
    if (!ctx.teamSelectMenu.isOpen()) {
      ctx.teamSelectMenu.show(15);
      return;
    }
  }

  // Buy menu toggle.
  // BUG-W-1: This is now the *only* listener that handles B for open/close —
  // the duplicate listener in BuyMenu used to race with this one (BuyMenu hid
  // first, then this handler saw `!isOpen()` and re-opened immediately).
  if (kb.isAction('buyMenu', e.code) && (ctx.state === 'playing' || ctx.state === 'dead')) {
    if (ctx.buyMenu.isOpen()) {
      ctx.buyMenu.hide();
    } else {
      ctx.buyMenu.setGrenadeInventory(ctx.grenadeInventory);
      ctx.buyMenu.show(ctx.money);
    }
    return;
  }

  // Leaderboard toggle
  if (kb.isAction('leaderboard', e.code) && (ctx.state === 'playing' || ctx.state === 'dead' || ctx.state === 'spectating' || ctx.state === 'menu')) {
    ctx.leaderboardPanel.toggle();
    return;
  }

  if (kb.isAction('netGraph', e.code) && (ctx.state === 'playing' || ctx.state === 'dead' || ctx.state === 'spectating' || ctx.state === 'menu')) {
    ctx.netGraph.toggle();
    return;
  }

  // HUD layout editor — only useful in touch builds (no-op on desktop since
  // the editor is only constructed when TouchButtons exists).
  if (kb.isAction('editHudLayout', e.code) && ctx.hudLayoutEditor) {
    if (ctx.hudLayoutEditor.isEditing()) {
      ctx.hudLayoutEditor.exitEditMode();
    } else {
      ctx.hudLayoutEditor.enterEditMode();
    }
    return;
  }

  if (ctx.buyMenu.isOpen()) return; // Let buy menu handle keys

  // C8 — radios are tactical communication; they should fire any time the
  // player is in a match, not only during canUseChat windows. Chat opens
  // (which steal keyboard focus to type) stay gated behind canUseChat so
  // we don't pop the chat box during menu / freeze transitions.
  if (kb.isAction('radioA', e.code)) {
    e.preventDefault();
    ctx.chatUI.openRadioMenu('z');
    return;
  }
  if (kb.isAction('radioB', e.code)) {
    e.preventDefault();
    ctx.chatUI.openRadioMenu('x');
    return;
  }
  if (kb.isAction('radioC', e.code)) {
    e.preventDefault();
    ctx.chatUI.openRadioMenu('c');
    return;
  }

  if (canUseChat) {
    if (kb.isAction('chat', e.code)) {
      e.preventDefault();
      ctx.chatUI.open('all');
      return;
    }
    if (kb.isAction('teamChat', e.code)) {
      e.preventDefault();
      ctx.chatUI.open('team');
      return;
    }
    if (ctx.chatUI.handleKeyDown(e)) {
      e.preventDefault();
      return;
    }
  }

  // ── Spectator controls ──
  if (ctx.state === 'spectating' && ctx.spectator.active) {
    // Jump key = cycle view mode in spectator
    if (kb.isAction('jump', e.code)) {
      e.preventDefault();
      ctx.spectator.cycleMode();
      return;
    }
    // Scoreboard
    if (kb.isAction('scoreboard', e.code)) {
      e.preventDefault();
      ctx.scoreboard.show();
      return;
    }
    // Arrow keys to cycle targets (not rebindable)
    if (e.code === 'ArrowRight') {
      ctx.spectator.requestNext(ctx.connection);
      return;
    }
    if (e.code === 'ArrowLeft') {
      ctx.spectator.requestPrev(ctx.connection);
      return;
    }
    // WASD/Ctrl/Shift are handled by the spectator system itself (for free-fly)
    return;
  }

  if (ctx.state !== 'playing') return;

  // Weapon inspect (F key)
  // Two complementary systems: the manual WeaponInspector (hold-and-orbit,
  // on the viewmodel) + the procedural InspectAnimationSystem (cycles
  // flip/checkChamber/spin/admire). Fire both on every F press — when
  // manual mode is active it visually takes over (procedural offsets are
  // gated off inside render()), and on the press that exits manual mode
  // the procedural animation plays on top of the now-free viewmodel.
  if (kb.isAction('inspect', e.code)) {
    ctx.viewmodel.toggleInspect();
    ctx.inspectAnimation.play();
    // W8: per-weapon-class inspect cue (falls back to universal weapon_inspect).
    playWeaponInspect(ctx.localPlayer.currentWeapon);
    return;
  }

  // Toggle FPP/TPP perspective
  if (kb.isAction('togglePerspective', e.code)) {
    togglePerspective(ctx);
    return;
  }

  // Scoreboard
  if (kb.isAction('scoreboard', e.code)) {
    e.preventDefault();
    ctx.scoreboard.show();
  }

  // Bomb interact (plant for T, defuse for CT) + door/pickup interact (Use).
  // Skip OS keyboard auto-repeat — the server's door toggle is edge-driven, so
  // repeated 'use' messages while E is held would flip Opening↔Closing every
  // tick and the door would never finish its swing.
  if (kb.isAction('interact', e.code)) {
    if (e.repeat) return;
    if (ctx.localPlayer.team === 1) {
      ctx.connection.send({ type: 'bomb_plant', data: {} });
    } else if (ctx.localPlayer.team === 2) {
      ctx.connection.send({ type: 'bomb_defuse', data: {} });
    }
    // Always send use action for doors/weapon pickup (server checks proximity)
    ctx.connection.send({ type: 'use', data: {} });
    return;
  }

  // Cycle grenades
  if (kb.isAction('cycleGrenade', e.code)) {
    cycleGrenade(ctx);
    return;
  }

  // Weapon slots — keybinds 1/2/3 look up the player's *currently-owned*
  // weapon in each slot. Pre-fix this was a hardcoded glock/usp/knife map,
  // which silently reverted bought rifles/pistols on slot key press because
  // glock/usp are always in OwnedWeapons.
  //
  // F-GP-018 (audits/2026-05-07): mobile uses `nextWeaponInCycle()` (skips
  // empty slots, wraps) while desktop uses this raw slot map (no cycle
  // binding exists yet — see `core/keybinds.ts` for the desktop key set).
  // If a desktop scroll-wheel `cycleWeaponForward/Backward` keybind is
  // ever added, route it through `nextWeaponInCycle` so empties are skipped
  // and behaviour matches the mobile WPN button.
  const slotMap: Array<['primary' | 'secondary' | 'melee', WeaponId | null]> = [
    ['primary', ctx.loadoutSlots.primary],
    ['secondary', ctx.loadoutSlots.secondary],
    ['melee', ctx.loadoutSlots.melee],
  ];
  for (const [action, weaponId] of slotMap) {
    if (kb.isAction(action, e.code)) {
      if (!weaponId) return; // empty slot — ignore the press
      if (ctx.selectedGrenade) {
        ctx.selectedGrenade = null;
        ctx.previousWeaponBeforeGrenade = null;
      }
      switchWeapon(ctx, weaponId);
      return;
    }
  }

  // Grenade slot (Digit4) — cycle grenades, don't switch weapon
  if (kb.isAction('grenades', e.code)) {
    cycleGrenade(ctx);
    return;
  }

  // C7 — Bomb slot (Digit5). Sends bomb_plant for the T-side carrier.
  // F-GP-014 (audits/2026-05-07): non-carriers get a short local hint
  // instead of a silent no-op; T-side behavior remains unchanged.
  //
  // BUG-W-6 (2026-05-17): on Digit5 a T-side *carrier* must also leave any
  // grenade/scope state so the player visually commits to planting. There is
  // no first-person C4 viewmodel asset in this codebase (the bomb only
  // appears as a back-prop on the TPP body via `attachTPPBomb`), so we can't
  // call `switchWeapon(ctx, 'c4')` — `c4` is not a valid `WeaponId` and the
  // viewmodel has no `c4` mesh registered. Instead we replicate the
  // "switching commits to the bomb" side-effects: force-unscope, drop the
  // selected grenade, and hide the grenade viewmodel so the next snapshot's
  // weapon state takes over cleanly. Carrier-vs-non-carrier is gated on
  // `ctx.localTppBombAttached`, which `game-network-handlers` keeps in sync
  // with the server's `isBombCarrier` flag.
  if (kb.isAction('bomb', e.code)) {
    if (ctx.localPlayer.team === 1 && ctx.localTppBombAttached) {
      if (ctx.isScoped) forceUnscope(ctx);
      if (ctx.selectedGrenade) {
        ctx.selectedGrenade = null;
        ctx.previousWeaponBeforeGrenade = null;
        ctx.viewmodel.hideGrenadeViewmodel();
      }
      ctx.connection.send({ type: 'bomb_plant', data: {} });
    } else {
      // TODO: Replace with a HUD toast when GameContext exposes a generic notification helper.
      console.warn('You do not have C4');
    }
    return;
  }
  // Also support Digit6 for awp (legacy slot 6)
  if (e.code === 'Digit6') {
    if (ctx.selectedGrenade) {
      ctx.selectedGrenade = null;
      ctx.previousWeaponBeforeGrenade = null;
    }
    switchWeapon(ctx, 'awp');
  }
}

export function handleKeyUp(ctx: GameContext, e: KeyboardEvent): void {
  if (isEditableTarget(e.target)) return;
  if (keybindManager.isAction('scoreboard', e.code)) {
    ctx.scoreboard.hide();
  }
  // No other keyup handling needed for spectator (free-fly keys are tracked by SpectatorSystem)
}

export function toggleScope(ctx: GameContext): void {
  const weaponId = ctx.weaponState.id;

  // Non-scoped weapons: bail
  if (getScopeType(weaponId) === 'none') return;

  // Use ScopeOverlay's enter() to advance the scope stage (handles double-scope for AWP)
  ctx.scopeOverlay.enter(weaponId);

  const isNowScoped = ctx.scopeOverlay.isActive();
  ctx.isScoped = isNowScoped;

  if (isNowScoped) {
    // Force FPP when scoping
    if (ctx.camera.getMode() === 'tpp') {
      ctx.camera.setMode('fpp');
    }
    // Set target FOV from scope overlay state (handles AWP double-scope, AUG/SG553 zoom)
    ctx.camera.camera.fov = ctx.scopeOverlay.getTargetFov();
    ctx.viewmodel.setVisible(false);
  } else {
    ctx.scopeOverlay.hide();
    ctx.camera.camera.fov = NORMAL_FOV;
    // Restore to saved camera mode
    const savedMode = settingsStore.get().cameraMode;
    ctx.camera.setMode(savedMode);
    ctx.viewmodel.setVisible(savedMode === 'fpp');
  }
  ctx.camera.camera.updateProjectionMatrix();
}

/**
 * Force-exit scope regardless of current stage.
 * Use this for weapon switch, reload, death, fire-unscope -- any non-user-initiated unscope.
 * toggleScope() cycles through stages (AWP double scope); this always fully exits.
 */
export function forceUnscope(ctx: GameContext): void {
  if (!ctx.isScoped) return;
  ctx.scopeOverlay.exit();
  ctx.isScoped = false;
  ctx.camera.camera.fov = NORMAL_FOV;
  ctx.camera.camera.updateProjectionMatrix();
  // Restore to saved camera mode
  const savedMode = settingsStore.get().cameraMode;
  ctx.camera.setMode(savedMode);
  ctx.viewmodel.setVisible(savedMode === 'fpp');
}

/** Apply settings from the settings store to all subsystems. */
export function applySettings(ctx: GameContext, s: GameSettings): void {
  // Camera mode
  ctx.camera.setMode(s.cameraMode);
  ctx.camera.setTPPParams(s.tppDistance, s.tppHeight, s.tppShoulder);
  ctx.camera.setSensitivity(s.sensitivity);

  // Viewmodel: hide in TPP, show in FPP (unless scoped)
  if (s.cameraMode === 'tpp') {
    ctx.viewmodel.setVisible(false);
  } else if (!ctx.isScoped) {
    ctx.viewmodel.setVisible(true);
  }

  // Local player body visibility: always show in TPP for full-body view
  // In FPP it stays visible (for look-down), but we don't change that

  // Graphics quality (guard: postProcessing may not exist during early init)
  ctx.postProcessing?.setQuality(s.graphicsQuality);
  ctx.volumetricSmoke?.setQuality(s.graphicsQuality);

  // Phase 8/9/10: Propagate quality to atmosphere, puddles, lightmaps, LOD
  ctx.atmosphere?.setQuality?.(s.graphicsQuality);
  ctx.puddleSystem?.setQuality?.(s.graphicsQuality);
  setLightmapQuality(s.graphicsQuality);
  setPropLODQuality(s.graphicsQuality);



  // Audio volumes
  audioManager.setMasterVolume(s.masterVolume);
  audioManager.setSfxVolume(s.sfxVolume);
  audioManager.setAnnouncerVolume(s.announcerVolume);
  audioManager.setFootstepVolume(s.footstepVolume);
  voiceChat.setVoiceVolume(s.voiceVolume);

  // R7 — colorblind CSS mode. Apply on every settings change AND on initial
  // boot (this function runs once during game.init after settings load) so
  // accessibility filters take effect without reopening the settings panel.
  applyColorblindCSS(s.colorblindMode);
}

/** Toggle between FPP and TPP perspective. */
export function togglePerspective(ctx: GameContext): void {
  // Force-exit scope when switching perspective (not a user scope toggle)
  if (ctx.isScoped) {
    forceUnscope(ctx);
  }

  const newMode = settingsStore.toggleCameraMode();
  // applySettings will be called by the onChange listener
}

export function buyWeapon(ctx: GameContext, weaponId: string): void {
  // Server-authoritative round phase check (if phase is known and not freeze_time, reject)
  if (ctx.roundPhase !== 'freeze_time' && ctx.roundPhase !== 'active') {
    // During warmup or round_end, buying may still be allowed — defer to server
  }

  const cfg = WEAPONS[weaponId as WeaponId];
  if (!cfg || cfg.price > ctx.money) return;

  ctx.money -= cfg.price;
  switchWeapon(ctx, weaponId as WeaponId);
  updateMoneyDisplay(ctx);

  ctx.connection.send({
    type: 'buy',
    data: { weaponId },
  });
}

/** Buy equipment (kevlar, helmet, defuse kit) — separate message from weapon buy. */
export function buyEquipment(ctx: GameContext, equipmentId: string, price: number): void {
  if (price > ctx.money) return;

  ctx.money -= price;
  updateMoneyDisplay(ctx);

  ctx.connection.send({
    type: 'buy_equipment',
    data: { equipmentId },
  });
}

export function updateMoneyDisplay(ctx: GameContext): void {
  const el = document.getElementById('money-display');
  if (el) el.textContent = `$${Math.floor(ctx.money)}`;
}

export function switchWeapon(ctx: GameContext, weaponId: WeaponId): void {
  if (ctx.weaponState.id === weaponId) return;
  // Cancel reload on weapon switch instead of blocking the switch

  // Force-exit scope when switching weapons (not a user toggle)
  if (ctx.isScoped) forceUnscope(ctx);

  // Cancel procedural inspect animation + reset spring recoil on weapon change
  ctx.inspectAnimation.onWeaponSwitch();
  ctx.recoilKick.reset();

  ctx.weaponState = createWeaponState(weaponId);
  ctx.localPlayer.equipWeapon(weaponId);
  ctx.viewmodel.switchWeapon(weaponId);
  ctx.hud.updateWeaponName(WEAPONS[weaponId].name);

  // Update TPP weapon on body if it exists
  if (ctx.localPlayerBody) {
    attachTPPWeapon(ctx.localPlayerBody, weaponId, {
      skinId: ctx.weaponLoadout.equipped[weaponId] ?? 0,
      skinLoader: ctx.weaponSkinLoader,
      stickers: ctx.weaponLoadout.stickers?.[weaponId],
      nameTag: ctx.weaponLoadout.nameTags?.[weaponId],
    });
  }

  ctx.connection.send({
    type: 'weapon_switch',
    data: { weaponId },
  });
}

// ── Grenade Helpers ──

/** Cycle through available grenades in inventory. */
export function cycleGrenade(ctx: GameContext): void {
  const available = getAvailableGrenades(ctx.grenadeInventory);
  if (available.length === 0) return;

  if (ctx.selectedGrenade === null) {
    // No grenade selected — select first available and remember current weapon
    ctx.previousWeaponBeforeGrenade = ctx.weaponState.id;
    ctx.selectedGrenade = available[0];
  } else {
    // Cycle to next grenade
    const idx = available.indexOf(ctx.selectedGrenade);
    const nextIdx = (idx + 1) % available.length;
    ctx.selectedGrenade = available[nextIdx];
  }

  // Null guard: selectedGrenade should be set by now, but guard against empty inventory race
  if (!ctx.selectedGrenade) return;

  // Update viewmodel to show grenade
  ctx.viewmodel.showGrenadeViewmodel();
  ctx.hud.updateWeaponName(GRENADES[ctx.selectedGrenade].name);
  ctx.hud.updateGrenadeSelection(ctx.selectedGrenade, ctx.grenadeInventory);
}

/** Throw the currently selected grenade. */
export function throwGrenade(ctx: GameContext, strength: number): void {
  if (!ctx.localPlayer.alive) return;
  if (!ctx.selectedGrenade) return;
  if (ctx.grenadeThrowAnimTimer > 0) return; // Already throwing

  const id = ctx.selectedGrenade;
  const count = ctx.grenadeInventory[id] ?? 0;
  if (count <= 0) return;

  // F-GP-012 (audits/2026-05-07): do NOT decrement `grenadeInventory[id]`
  // here. The previous code deducted locally before the server confirmed the
  // throw, which produced an HUD flicker (and a brief "can't re-throw" gap)
  // any time the server rejected the throw. The next snapshot from the
  // server (`pData.grenades` rehydrate) is the authoritative source — we
  // start the throw animation immediately, but defer the count change until
  // the snapshot arrives.

  // Start throw animation
  ctx.grenadeThrowAnimTimer = 0.5; // 0.5s throw animation

  // Send throw message to server
  ctx.connection.send({
    type: 'grenade_throw',
    data: {
      grenadeType: id,
      throwStrength: strength,
      yaw: ctx.localPlayer.yaw,
      pitch: ctx.localPlayer.pitch,
    },
  });

  // Trigger viewmodel throw animation (pullback → forward → release)
  ctx.viewmodel.startGrenadeThrow();

  // Remember previous weapon to switch back
  if (!ctx.previousWeaponBeforeGrenade) {
    ctx.previousWeaponBeforeGrenade = ctx.weaponState.id;
  }

  // Update HUD — selection stays on the same grenade until the next
  // snapshot drives the count to 0, at which point the snapshot path
  // (game-network-handlers) will re-route to the next available grenade.
  ctx.hud.updateGrenadeSelection(ctx.selectedGrenade, ctx.grenadeInventory);
}

/** Buy a grenade: deduct money, add to inventory, send server message. */
export function buyGrenade(ctx: GameContext, grenadeId: GrenadeId): void {
  const cfg = GRENADES[grenadeId];
  if (!cfg || cfg.price > ctx.money) return;

  ctx.money -= cfg.price;
  ctx.grenadeInventory[grenadeId] = (ctx.grenadeInventory[grenadeId] ?? 0) + 1;
  updateMoneyDisplay(ctx);

  ctx.connection.send({
    type: 'buy_grenade',
    data: { grenadeType: grenadeId },
  });

  // Update HUD
  ctx.hud.updateGrenadeSelection(ctx.selectedGrenade, ctx.grenadeInventory);
}
